import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'
import { FC, useEffect } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Workflow } from '../../types/types'
import { CheckboxStyle } from '../../utils/styling-constants'
import Modal from '../Modal'

type WorkflowEditorProps = {
  mode: string
  initWorkflow: Workflow
  onConfirm: (workflow: Workflow, mode: string) => void
  onCancel: () => void
}

const editorTitle = {
  new: 'Create New Workflow',
  edit: 'View/Edit Workflow'
}

const WorkflowEditor: FC<WorkflowEditorProps> = ({ mode, initWorkflow, onConfirm, onCancel }) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<Workflow>()

  useEffect(() => {
    setValue('name', initWorkflow.name)
    setValue('description', initWorkflow.description)
    setValue('dependent', initWorkflow.dependent || false)
  }, [])

  const getFormErrorMessage = name => {
    return errors[name] ? (
      <small className="error">{errors[name].message}</small>
    ) : (
      <small className="error">&nbsp;</small>
    )
  }

  const onSubmit: SubmitHandler<Workflow> = (formData: Workflow) => {
    const newWorkflow = {
      ...formData
    } as Workflow
    if (mode === 'new') {
      newWorkflow._id = ''
      onConfirm && onConfirm(newWorkflow, mode)
    } else if (mode === 'edit') {
      newWorkflow._id = initWorkflow._id
      onConfirm && onConfirm(newWorkflow, mode)
    }
  }

  const handleConfirm = () => {
    onCancel && onCancel()
  }

  return (
    <Modal
      size="md"
      isOpen={true}
      withAction={false}
      easyClose={true}
      onCancel={() => onCancel && onCancel()}
      title={editorTitle[mode]}
      showCancelButton={true}
      zIndex={30}
      onConfirm={handleConfirm}>
      <div className="relative">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full grid grid-cols-2 gap-x-10 gap-y-4 mt-8">
          <div className="field-box col-span-2">
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Name is required.' }}
              defaultValue={initWorkflow && initWorkflow.name}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <InputText
                    id={field.name}
                    value={field.value}
                    className={classNames({ error: fieldState.error })}
                    onChange={e => field.onChange(e.target.value)}
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>
          <div className="field-box col-span-2">
            <Controller
              name="description"
              control={control}
              defaultValue={initWorkflow && initWorkflow.description}
              rules={{ required: 'Description is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <InputText
                    id={field.name}
                    {...field}
                    className={classNames({ error: fieldState.error })}
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>
          <div className="field-box col-span-2">
            <Controller
              name="dependent"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex-grow flex items-center">
                  <Checkbox
                    inputId={field.name}
                    pt={CheckboxStyle}
                    checked={field.value}
                    onChange={e => {
                      field.onChange(e.checked)
                    }}></Checkbox>
                  <label htmlFor={field.name} className="ml-2 font-normal">
                    Dependent
                  </label>
                </div>
              )}
            />
          </div>

          <div className="flex items-center justify-end space-x-4 col-span-3">
            <Button type="submit" label="Save" />
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default WorkflowEditor
